import { TooltipContentProps, TooltipProviderProps } from '@radix-ui/react-tooltip';
import { ComponentProps, ReactNode } from 'react';
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from './tooltip-primitives';

/**
 * Props for the Tooltip component
 * @property {ReactNode} trigger - The element that triggers the tooltip
 * @property {ReactNode} content - The content to display in the tooltip
 * @property {TooltipContentProps['align']} [contentAlign='start'] - Alignment of the tooltip content
 * @property {TooltipProviderProps['delayDuration']} [delayDuration=200] - Delay before showing the tooltip (in ms)
 * @property {string} [triggerClassName] - CSS class for the trigger element
 * @property {string} [contentClassName] - CSS class for the content element
 *
 * @see https://www.radix-ui.com/primitives/docs/components/tooltip - Check Radix UI Tooltip documentation for more options and functionality
 */

type TooltipProps = {
  trigger: ReactNode;
  content: ReactNode;
  contentAlign?: TooltipContentProps['align'];
  delayDuration?: TooltipProviderProps['delayDuration'];
  triggerClassName?: string;
  contentClassName?: string;
  arrowConfig?: ComponentProps<typeof TooltipContent>['arrowConfig'];
};

/**
 * Tooltip component that displays content when hovering over a trigger element
 *
 * @param {TooltipProps} props - Component props
 * @returns {JSX.Element} Tooltip component
 *
 * @see https://www.radix-ui.com/primitives/docs/components/tooltip - Check Radix UI Tooltip documentation for more options and functionality
 */

export const Tooltip = ({
  trigger,
  content,
  contentAlign = 'start',
  delayDuration = 200,
  triggerClassName,
  contentClassName,
  arrowConfig,
}: TooltipProps) => {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger className={triggerClassName}>{trigger}</TooltipTrigger>

        <TooltipContent align={contentAlign} className={contentClassName} arrowConfig={arrowConfig}>
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};
